import { Link, useNavigate } from "react-router-dom";
import {
  Divider, NoData, Spinner
} from "@whitespace/whitespace-ui";
import { useCallback, useEffect, useState } from "react";
import {
  Breadcrumb, Button, Card, Col, ListGroup, Row
} from "react-bootstrap";
import SearchEntry from "components/SearchEntry/SearchEntry";

import { FunnelFill } from "react-bootstrap-icons";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { useStateMachine } from "little-state-machine";
import { IActivityResponseItem } from "interfaces/responses/IActivityResponseItem";
import { mapActivityResponseToUserHistoryItem } from "mappers/mapActivityResponseToUserHistoryItem/mapActivityResponseToUserHistoryItem";
import { IUserHistoryItem } from "interfaces/models/IUserHistoryItem";
import { updateSelectedUser } from "actions/updateSelectedUser";
import { updateIsUserHistoryFilterDrawerOpen } from "actions/updateIsUserHistoryFilterDrawerOpen";
import { updateUserHistoryFilter } from "actions/updateUserHistoryFilter";
import { updateSitesFiltered } from "actions/updateSitesFiltered";
import { updateUserHistoryFilterDates } from "actions/updateUserHistoryFilterDates";

import { format } from "date-fns";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { updateActionsFiltered } from "actions/updateActionsFiltered";
import UserHistoryItem from "./UserHistoryItem/UserHistoryItem";
import UserHistoryFilterDrawer from "./UserHistoryFilterDrawer/UserHistoryFilterDrawer";

import "./UserHistory.scss";

const UserHistory = () => {
  const { state } = useStateMachine();
  const [userHistoryItems, setUserHistoryItems] = useState<IUserHistoryItem[] | undefined>(undefined);
  const [filteredUserHistoryItems, setFilteredUserHistoryItems] = useState<IUserHistoryItem[] | undefined>(undefined);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const navigate = useNavigate();

  const { actions } = useStateMachine({
    updateIsUserHistoryFilterDrawerOpen,
    updateSelectedUser,
    updateUserHistoryFilter,
    updateSites: updateSitesFiltered,
    updateActionsFiltered,
    updateUserHistoryFilterDates
  });

  const filterUserHistory = () => {
    if (userHistoryItems) {
      let userHistoryItemsFiltered = userHistoryItems;

      if (state.userHistoryFilter!.searchText) {
        const searchTerm = new RegExp(state.userHistoryFilter!.searchText, "gi");
        userHistoryItemsFiltered = userHistoryItemsFiltered.filter((userHistoryItem : IUserHistoryItem) => userHistoryItem.action.match(searchTerm)
          || userHistoryItem.details.match(searchTerm)
          || userHistoryItem.site?.match(searchTerm)
          || userHistoryItem.ipAddress?.match(searchTerm));
      }

      if (state.userHistoryFilter!.actions.length > 0) {
        userHistoryItemsFiltered = userHistoryItemsFiltered.filter(userHistoryItem => {
          if (state.userHistoryFilter!.actions.includes(userHistoryItem.action)) {
            return userHistoryItem;
          }

          return undefined;
        });
      }

      if (state.userHistoryFilter!.sites.length > 0) {
        userHistoryItemsFiltered = userHistoryItemsFiltered.filter(userHistoryItem => {
          if (state.userHistoryFilter!.sites.includes(userHistoryItem.site)) {
            return userHistoryItem;
          }

          return undefined;
        });
      }

      if (state.userHistoryFilter!.logDate) {
        userHistoryItemsFiltered = userHistoryItemsFiltered
          .filter(userHistoryItem => format(new Date(userHistoryItem.timeStamp), "yyyy-MM-dd") === state.userHistoryFilter!.logDate);
      }

      const sitesFiltered = userHistoryItemsFiltered
        .flatMap(historyItem => historyItem.site!)
        .filter(
          (item, idx, arr) => arr.findIndex(t => t === item) === idx && item
        )
        .sort((a, b) => a?.localeCompare(b));

      actions.updateSites(sitesFiltered);

      const actionsFiltered = userHistoryItemsFiltered
        .flatMap(historyItem => historyItem.action!)
        .filter(
          (item, idx, arr) => arr.findIndex(t => t === item) === idx && item !== null
        )
        .sort((a, b) => a?.localeCompare(b));

      actions.updateActionsFiltered(actionsFiltered);

      const userHistoryDates = userHistoryItemsFiltered
        .map(userHistoryItem => format(new Date(userHistoryItem.timeStamp), "yyyy-MM-dd"))
        .filter(
          (item, idx, arr) => arr.findIndex(t => t === item) === idx
        )
        .sort((a, b) => a?.localeCompare(b));

      actions.updateUserHistoryFilterDates(userHistoryDates);

      setFilteredUserHistoryItems(userHistoryItemsFiltered);
    }
  };

  const {
    isSending, errors, data, sendRequest
  } = useAmplifyREST();

  useEffect(() => {
    const url = `users/${encodeURIComponent(state.selectedUser!.userId)}/activities`;
    sendRequest(url, "GET", null);
  }, [sendRequest]);

  useEffect(() => {
    if (data) {
      const userHistoryItemList = data.map((activityResponseItem : IActivityResponseItem) => mapActivityResponseToUserHistoryItem(activityResponseItem));
      setUserHistoryItems(userHistoryItemList);
      setFilteredUserHistoryItems(userHistoryItemList);
    }
  }, [data]);

  useEffect(() => {
    if (!isFilterApplied && userHistoryItems) {
      filterUserHistory();
      setIsFilterApplied(true);
    }
  }, [isFilterApplied, userHistoryItems]);

  const onCloseMenu = useCallback(() => {
    actions.updateIsUserHistoryFilterDrawerOpen(false);
  }, [actions]);

  const renderUserHistoryItems = () => {
    if (filteredUserHistoryItems) {
      return filteredUserHistoryItems.map((userHistoryItem: IUserHistoryItem) => (
        <UserHistoryItem key={userHistoryItem.id} userHistoryItem={userHistoryItem} />));
    }

    return (<></>);
  };

  useEffect(() => {
    if (state.userHistoryFilter) {
      filterUserHistory();
    }
  }, [state.userHistoryFilter]);

  const handleTextSearch = (searchText: string) => {
    if (state.userHistoryFilter) {
      const userHistoryFilter = { ...state.userHistoryFilter };
      userHistoryFilter!.searchText = searchText;
      actions.updateUserHistoryFilter(userHistoryFilter);
    }
  };

  const handleNavToUsers = () => {
    actions.updateSelectedUser(undefined);
    navigate("/users");
  };

  useEffect(() => {
    if (!state.selectedUser) {
      navigate("/users");
    }
  }, [state.selectedUser]);

  return (
    <>
      <UserHistoryFilterDrawer isOpen={state.isUserHistoryFilterDrawerOpen} onClose={onCloseMenu} />
      {errors && <AmplifyError errors={errors} />}

      <Card>
        <Card.Header>
          <Row className="w-100">
            <Col xs={8}>
              <h1>
                User History
                <Spinner animation="grow" visible={isSending} spinnerText="Loading" variant="info" />
              </h1>
            </Col>
            <Col xs={4} className="d-flex justify-content-end">
              <span className="selected-user-title">{state.selectedUser?.username}</span>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={4} className="d-flex">
              <Breadcrumb className="breadcrumb-user-history left-margin-large">
                <Breadcrumb.Item data-testid="breadcrumb-users" linkAs={Link} linkProps={{ to: "/" }} onClick={handleNavToUsers}>Users</Breadcrumb.Item>
                <Breadcrumb.Item active>User History</Breadcrumb.Item>
              </Breadcrumb>
            </Col>
            <Col xs={8} className="d-flex justify-content-end user-filter">
              <Button variant="primary" onClick={handleNavToUsers}>
                Back
              </Button>
            </Col>
          </Row>
          <Divider />
          <Row className="w-100 align-items-center bottom-margin-small top-margin-small">
            <Col xs={{ span: 8, offset: 4 }} className="d-flex justify-content-end user-filter">
              <SearchEntry defaultValue={state.userHistoryFilter!.searchText!} onSearch={handleTextSearch} />
              <button
                data-testid="btnUserFilter"
                className="btn btn-filter"
                name="filterButton"
                type="button"
                aria-label="Toggle filter options"
                onClick={() => { actions.updateIsUserHistoryFilterDrawerOpen(true); }}
              >
                <FunnelFill size={30} data-testid="filterButton" />
              </button>
            </Col>
          </Row>
          <Divider />
          <Row className="list-heading">
            <Col data-testid="actionHeader">Action</Col>
            <Col data-testid="detailsHeader">Details</Col>
            <Col data-testid="siteHeader" className="d-none d-md-block">Site</Col>
            <Col data-testid="ipHeader" className="d-none d-lg-block">IP</Col>
            <Col data-testid="dateTimeHeader">Date/Time</Col>
          </Row>
          <Divider className="no-display-sm" />
          {filteredUserHistoryItems
                  && (
                    <>
                      <NoData visible={filteredUserHistoryItems.length === 0} description="No User History Found" />
                      <ListGroup variant="flush" className="striped highlight">
                        {renderUserHistoryItems()}
                      </ListGroup>
                    </>
                  )}
        </Card.Body>
      </Card>
    </>
  );
};

export default UserHistory;
